import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, BarChart3, Award, BookOpen, CreditCard, Settings, LogOut, ArrowLeft, Moon, Sun } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import Logo from './Logo';
import { AdminLink } from './AdminLink';
import { MobileNav } from './ui/MobileNav';

export interface AdultLayoutProps {
  className?: string;
}

export function AdultLayout({ className }: AdultLayoutProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();

  const navigation = [
    { name: 'Overview', href: '/adult', icon: Home },
    { name: 'Children', href: '/adult/children', icon: Users },
    { name: 'Progress', href: '/adult/progress', icon: BarChart3 },
    { name: 'Achievements', href: '/adult/achievements', icon: Award },
    { name: 'Activities', href: '/adult/activities', icon: BookOpen },
    { name: 'Billing', href: '/adult/billing', icon: CreditCard },
    { name: 'Settings', href: '/adult/settings', icon: Settings },
  ];

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'
    } ${className || ''}`}>
      {/* Header */}
      <header className={`shadow-sm border-b transition-colors duration-200 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/portal')}
                className={`flex items-center space-x-2 px-4 py-2 text-sm transition-all duration-300 rounded-full border shadow-sm backdrop-blur-sm ${
                  isDarkMode
                    ? 'text-gray-300 hover:text-white bg-gray-700/60 hover:bg-gray-600 border-gray-600 hover:border-gray-500 hover:shadow-md'
                    : 'text-blue-600 hover:text-blue-700 bg-white/60 hover:bg-blue-50 border-blue-200 hover:border-blue-300 hover:shadow-md'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Portal</span>
              </button>
              
              <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
              
              <Logo size="md" variant="horizontal" className={isDarkMode ? 'text-white' : 'text-gray-900'} />
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Admin Link */}
              <AdminLink />

              {/* Mobile Navigation */}
              <MobileNav navigation={navigation} isDarkMode={isDarkMode} />

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              
              <span className={`text-sm hidden sm:block ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Parent Dashboard
              </span>
              <button
                onClick={handleSignOut}
                className={`flex items-center space-x-2 px-3 py-1.5 text-sm transition-colors ${
                  isDarkMode
                    ? 'text-gray-300 hover:text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:block">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className={`hidden md:block shadow-sm transition-colors duration-200 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 text-sm font-medium transition-colors whitespace-nowrap font-baloo ${
                    isActive
                      ? 'border-purple-500 text-purple-600'
                      : isDarkMode
                      ? 'border-transparent text-gray-300 hover:text-white hover:border-gray-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}